import React, { useState, useEffect } from 'react';
import { MessageCircle, Archive, Brain, CheckSquare, Plus, Send, Globe, X } from 'lucide-react';

// Update this to your actual Worker URL
const API_URL = 'https://orkway-api.b-9f2.workers.dev/api';

export default function ConversationManager({ 
  initialQuestion, 
  initialResponse, 
  onSessionComplete 
}) {
  const [conversations, setConversations] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [speaker, setSpeaker] = useState('Me');
  const [loading, setLoading] = useState(false);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [newConversationData, setNewConversationData] = useState({
    title: '',
    description: '',
    language: 'en'
  });
  const [error, setError] = useState('');

  // Fetch conversations
  useEffect(() => {
    fetchConversations();
    fetchActionItems();
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/conversations`);
      if (!response.ok) throw new Error('Failed to fetch conversations');
      const data = await response.json();
      setConversations(data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Failed to load conversations. Check console for details.');
    }
  };

  const fetchActionItems = async () => {
    try {
      const response = await fetch(`${API_URL}/action-items`);
      if (!response.ok) throw new Error('Failed to fetch action items');
      const data = await response.json();
      setActionItems(data);
    } catch (error) {
      console.error('Error fetching action items:', error);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setError('');
    try {
      const response = await fetch(`${API_URL}/conversations/${conv.id}`);
      if (!response.ok) throw new Error('Failed to fetch conversation details');
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching conversation details:', error);
      setError('Failed to load conversation details.');
    }
  };

  const createConversation = async () => {
    if (!newConversationData.title.trim()) {
      alert('Please enter a conversation title');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newConversationData)
      });
      
      if (!response.ok) throw new Error('Failed to create conversation');
      
      const newConv = await response.json();
      setShowNewConversationModal(false);
      setNewConversationData({ title: '', description: '', language: 'en' });
      await fetchConversations();
      selectConversation(newConv);
    } catch (error) {
      console.error('Error creating conversation:', error);
      setError('Failed to create conversation. Check console for details.');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const response = await fetch(`${API_URL}/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newMessage, speaker })
      });
      
      if (!response.ok) throw new Error('Failed to send message');
      
      setNewMessage('');
      await selectConversation(selectedConversation);
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message.');
    }
  };

  const analyzeConversation = async () => {
    if (!selectedConversation) return;
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/conversations/${selectedConversation.id}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'all' })
      });
      
      if (!response.ok) throw new Error('Failed to analyze conversation');
      
      const result = await response.json();
      
      // Try to parse the results if they're a string
      let analysisResults;
      try {
        analysisResults = typeof result.results === 'string' 
          ? JSON.parse(result.results) 
          : result.results;
      } catch (e) {
        analysisResults = result.results;
      }
      
      // Show results in a better way
      if (analysisResults) {
        alert(`Analysis Complete!\n\nVerbs found: ${analysisResults.verbs?.join(', ') || 'None'}\n\nKey phrases: ${analysisResults.keyPhrases?.join(', ') || 'None'}\n\nSummary: ${analysisResults.summary || 'No summary'}`);
      }
      
      await fetchActionItems();
    } catch (error) {
      console.error('Error analyzing conversation:', error);
      setError('Failed to analyze conversation.');
    } finally {
      setLoading(false);
    }
  };

  const translateMessage = async (messageId) => {
    const targetLang = window.prompt('Enter target language (e.g., Spanish, French):');
    if (!targetLang) return;

    try {
      const response = await fetch(`${API_URL}/messages/${messageId}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetLanguage: targetLang })
      });
      
      if (!response.ok) throw new Error('Failed to translate');
      
      const result = await response.json();
      await selectConversation(selectedConversation);
    } catch (error) {
      console.error('Error translating message:', error);
      setError('Failed to translate message.');
    }
  };

  const archiveConversation = async (convId) => {
    if (!window.confirm('Archive this conversation?')) return;
    
    try {
      const response = await fetch(`${API_URL}/conversations/${convId}/archive`, {
        method: 'PATCH'
      });
      
      if (!response.ok) throw new Error('Failed to archive');
      
      await fetchConversations();
      if (selectedConversation?.id === convId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error archiving conversation:', error);
      setError('Failed to archive conversation.');
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Error Banner */}
      {error && (
        <div className="absolute top-0 left-0 right-0 bg-red-500 text-white p-3 text-center">
          {error}
          <button onClick={() => setError('')} className="ml-4 underline">Dismiss</button>
        </div>
      )}

      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">New Conversation</h3>
              <button
                onClick={() => setShowNewConversationModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={newConversationData.title}
                  onChange={(e) => setNewConversationData({...newConversationData, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Spanish Lesson 1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newConversationData.description}
                  onChange={(e) => setNewConversationData({...newConversationData, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Optional description..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Language
                </label>
                <select
                  value={newConversationData.language}
                  onChange={(e) => setNewConversationData({...newConversationData, language: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="it">Italian</option>
                  <option value="pt">Portuguese</option>
                  <option value="zh">Chinese</option>
                  <option value="ja">Japanese</option>
                  <option value="ko">Korean</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={createConversation}
                className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
              >
                Create
              </button>
              <button
                onClick={() => setShowNewConversationModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div className="w-80 bg-white border-r overflow-y-auto">
        <div className="p-4 border-b">
          <button
            onClick={() => setShowNewConversationModal(true)}
            className="w-full bg-blue-500 text-white rounded-lg px-4 py-2 flex items-center justify-center gap-2 hover:bg-blue-600"
          >
            <Plus size={20} />
            New Conversation
          </button>
        </div>

        <div className="p-4">
          <h3 className="font-semibold mb-3 text-gray-700">Active Conversations</h3>
          {conversations.length === 0 ? (
            <p className="text-gray-500 text-sm">No conversations yet. Create one to get started!</p>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => selectConversation(conv)}
                className={`p-3 rounded-lg mb-2 cursor-pointer transition-colors ${
                  selectedConversation?.id === conv.id
                    ? 'bg-blue-50 border-blue-300 border'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageCircle size={18} className="text-gray-400" />
                    <span className="font-medium">{conv.title}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveConversation(conv.id);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Archive size={16} />
                  </button>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {conv.message_count || 0} messages • {conv.action_count || 0} actions
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-4 border-t">
          <h3 className="font-semibold mb-3 text-gray-700">Pending Actions</h3>
          {actionItems.length === 0 ? (
            <p className="text-gray-500 text-sm">No pending actions</p>
          ) : (
            actionItems.slice(0, 5).map((item) => (
              <div key={item.id} className="p-2 text-sm mb-2 bg-yellow-50 rounded">
                <div className="flex items-start gap-2">
                  <CheckSquare size={16} className="text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-gray-700">{item.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      From: {item.conversation_title}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Header */}
            <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">{selectedConversation.title}</h2>
              <button
                onClick={analyzeConversation}
                disabled={loading || messages.length === 0}
                className="bg-purple-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Brain size={18} />
                {loading ? 'Analyzing...' : 'Analyze'}
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 mt-10">
                  <p>No messages yet. Start the conversation below!</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className="mb-4">
                    <div className={`flex ${msg.speaker === 'Me' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-2xl px-4 py-2 rounded-lg ${
                          msg.speaker === 'Me'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-800'
                        }`}
                      >
                        <div className="font-semibold text-sm mb-1">{msg.speaker}</div>
                        <div>{msg.content}</div>
                        {msg.translation && (
                          <div className="mt-2 pt-2 border-t border-opacity-20 text-sm italic">
                            {msg.translation}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => translateMessage(msg.id)}
                      className="mt-1 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 ml-2"
                    >
                      <Globe size={12} />
                      Translate
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Input */}
            <div className="bg-white border-t p-4">
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={speaker}
                  onChange={(e) => setSpeaker(e.target.value)}
                  placeholder="Speaker name"
                  className="px-3 py-2 border rounded-lg text-sm w-32"
                />
                <span className="text-gray-500 self-center">is speaking:</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={sendMessage}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageCircle size={48} className="mx-auto mb-4" />
              <p>Select a conversation or create a new one</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}